import type { HelpCenterStructureNode, HomepagePinRef } from '../types';

export function getNodePinRef(
  node: HelpCenterStructureNode
): HomepagePinRef | null {
  if (node.type === 'category' && node.category?.id != null) {
    return { type: 'category', id: String(node.category.id) };
  }
  if (node.type === 'item' && node.item?.id != null) {
    return { type: 'item', id: String(node.item.id) };
  }
  return null;
}

export function pinRefKey(pin: HomepagePinRef): string {
  return `${pin.type}:${pin.id}`;
}

export function isStructureNodePublished(
  node: HelpCenterStructureNode
): boolean {
  if (node.type === 'category') {
    return node.category?.is_published !== false;
  }
  return node.item?.is_published !== false;
}

export function buildStructureLookup(
  structure: HelpCenterStructureNode[]
): Map<string, HelpCenterStructureNode> {
  const map = new Map<string, HelpCenterStructureNode>();

  const walk = (nodes: HelpCenterStructureNode[]) => {
    for (const node of nodes) {
      const ref = getNodePinRef(node);
      if (ref) {
        map.set(pinRefKey(ref), node);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(structure);
  return map;
}

export function flattenStructureNodes(
  structure: HelpCenterStructureNode[],
  parentTitle = ''
): Array<{ node: HelpCenterStructureNode; breadcrumb: string }> {
  const items: Array<{ node: HelpCenterStructureNode; breadcrumb: string }> = [];

  for (const node of structure) {
    const title =
      node.type === 'category'
        ? node.category?.title || 'Untitled category'
        : node.item?.item_type === 'article'
          ? node.item?.article?.title || 'Untitled article'
          : node.item?.guide?.title || 'Untitled guide';

    const breadcrumb = parentTitle ? `${parentTitle} / ${title}` : title;
    items.push({ node, breadcrumb });

    if (node.children?.length) {
      items.push(...flattenStructureNodes(node.children, breadcrumb));
    }
  }

  return items;
}

export function resolveHomepagePins(
  pins: HomepagePinRef[] | undefined,
  structure: HelpCenterStructureNode[]
): Array<{ pin: HomepagePinRef; node: HelpCenterStructureNode | null }> {
  if (!pins?.length) return [];

  const lookup = buildStructureLookup(structure);

  return pins.map((pin) => ({
    pin,
    node: lookup.get(pinRefKey(pin)) || null,
  }));
}

export function isPinned(
  pins: HomepagePinRef[] | undefined,
  ref: HomepagePinRef
): boolean {
  return (pins || []).some(
    (pin) => pin.type === ref.type && String(pin.id) === String(ref.id)
  );
}

export function removePinsForDeletedNode(
  pins: HomepagePinRef[],
  node: HelpCenterStructureNode
): HomepagePinRef[] {
  const refsToRemove = new Set<string>();

  const collectRefs = (current: HelpCenterStructureNode) => {
    const ref = getNodePinRef(current);
    if (ref) refsToRemove.add(pinRefKey(ref));
    current.children?.forEach(collectRefs);
  };

  collectRefs(node);

  return pins.filter((pin) => !refsToRemove.has(pinRefKey(pin)));
}

export function countCategoryItems(node: HelpCenterStructureNode): {
  articles: number;
  guides: number;
} {
  let articles = 0;
  let guides = 0;

  const walk = (nodes: HelpCenterStructureNode[] | undefined) => {
    if (!nodes) return;
    for (const child of nodes) {
      if (child.type === 'item') {
        if (child.item?.item_type === 'article') articles++;
        if (child.item?.item_type === 'guide') guides++;
      } else if (child.type === 'category') {
        walk(child.children);
      }
    }
  };

  walk(node.children);
  return { articles, guides };
}
